import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMutationSubscriptionChangeSchedule,
  useQueryMemberGetCurrentMembershipSubscriptionAsMember,
} from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import useGymflowModels from "apps/portal/src/store";
import { isAxiosError } from "axios";
import { FormikProvider, useFormik } from "formik";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { usePortalRoutes } from "../../../hooks";
import { RouteFeature } from "../../../routes";
import {
  Button,
  InfoBlock,
  Spinner,
  WarningBlock,
  WarningSection,
} from "../../atoms";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";
import { FormNewMembershipAsMemberSelect } from "./components/FormNewMembershipAsMemberSelect";
import { FormScheduledDateAsMemberSelect } from "./components/FormScheduledDateAsMemberSelect";
import {
  changeMembershipFormSchema,
  INITIAL_VALUES_CHANGE_MEMBERSHIP_FORM,
} from "./constants";
import { ChangeMembershipFormValues } from "./types";

export const HostedPagesProfileChangeMembership = () => {
  const { t } = useTranslation();
  const { isEmbed } = usePortalRoutes();
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const [parent] = useAutoAnimate();
  const { api } = useGymflowModels();
  const history = useHistory();
  const { notifyDanger, toast } = useContext(ToastContext);

  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      RouteFeature.SiteBuyMembershipType.replace(":membershipType", "all"),
    ),
  );

  const {
    data: activeMembershipSubscription,
    isLoading: isLoadingActiveMembershipSubscription,
  } = useQueryMemberGetCurrentMembershipSubscriptionAsMember(
    {
      api,
    },
    { refetchOnWindowFocus: true },
  );
  const { mutateAsync: mutateAsyncSubscriptionChangeSchedule } =
    useMutationSubscriptionChangeSchedule({
      api,
      subscriptionId: activeMembershipSubscription?.id ?? 0,
    });

  const formik = useFormik<ChangeMembershipFormValues>({
    initialValues: INITIAL_VALUES_CHANGE_MEMBERSHIP_FORM,
    validationSchema: changeMembershipFormSchema,
    onSubmit: async ({ newMembership, date }) => {
      if (!newMembership || !date) {
        return;
      }
      try {
        await mutateAsyncSubscriptionChangeSchedule({
          membershipId: newMembership?.value.id,
          changeDate: date.value,
        });
        toast({
          message: t(
            "page.hostedPagesProfileChangeMembership.toast.changingScheduled",
          ),
          intent: "success",
          type: "small",
        });
        history.replace(createSiteOrEmbedLink(RouteFeature.SiteProfile));
      } catch (e) {
        if (isAxiosError(e)) {
          notifyDanger(e);
        }
      }
    },
  });

  return (
    <div
      className={cn("track-height flex w-full flex-col overflow-y-auto", {
        "h-fit": isEmbed,
        "h-full": !isEmbed,
      })}
    >
      <HostedPagesBackHeader
        label={t("page.hostedPagesProfileChangeMembership.header")}
      />
      <div
        ref={parent}
        className={cn(
          "flex flex-col items-center bg-gray-50 dark:bg-gray-900 px-4 py-6",
          {
            "h-fit": isEmbed,
            "h-full": !isEmbed,
          },
        )}
      >
        {isLoadingActiveMembershipSubscription ? (
          <Spinner />
        ) : (
          <div className="flex max-w-lg flex-col gap-11">
            <FormikProvider value={formik}>
              <div className="flex flex-col gap-4">
                <WarningBlock
                  text={t(
                    "page.hostedPagesProfileChangeMembership.warningBlock.partOfShared",
                  )}
                />
                {activeMembershipSubscription && (
                  <>
                    <FormNewMembershipAsMemberSelect
                      activeMembership={activeMembershipSubscription}
                    />
                    <FormScheduledDateAsMemberSelect
                      activeMembership={activeMembershipSubscription}
                    />
                  </>
                )}
                <InfoBlock
                  text={t(
                    "page.hostedPagesProfileChangeMembership.infoBlock.onlySchedule",
                  )}
                />
              </div>
              <div className="flex flex-col gap-6">
                {formik.values.newMembership &&
                  activeMembershipSubscription?.billingType !==
                    formik.values.newMembership?.value.billingType && (
                    <WarningSection
                      label={t(
                        "page.hostedPagesProfileChangeMembership.warningSection.billingDateChange.label",
                      )}
                      subLabel={t(
                        "page.hostedPagesProfileChangeMembership.warningSection.billingDateChange.subLabel",
                      )}
                    />
                  )}
                <div className="flex gap-4">
                  <Button
                    intent="default"
                    className="w-full"
                    onClick={() => history.goBack()}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    intent="secondary"
                    className="w-full"
                    onClick={async () => await formik.submitForm()}
                  >
                    {t("common.confirm")}
                  </Button>
                </div>
              </div>
            </FormikProvider>
          </div>
        )}
      </div>
    </div>
  );
};
